// DifficultyLegend — kompakte Pille im BottomBar; Tap öffnet Bottom-Sheet
// mit Filter + Erklärungen.
//
// Wave 14ca-fix (User-Feedback nach erster Iteration):
//   "Difficulty-Filter direkt auf der Karte tappen ist auf kleinen Handys
//    nicht treffbar. Mach das wieder wie früher: aufklappbar (collapsed by
//    default, expand on tap), und dann drinnen 'clever gemanaged' wie man
//    filtert."
//
// Layout-Strategie:
//   1. Im BottomBar zeigen wir den 7-Segment-Regenbogen wieder als reine
//      Display-Pille (keine per-Segment-Tappable-Buttons mehr — die waren
//      auf 16 px zu schmal für saubere Touch-Targets). Die ganze Pille ist
//      EIN grosser Tap-Button (~26 × 100 px).
//   2. Tap auf die Pille öffnet das Bottom-Sheet mit:
//        - Quick-Presets (All / None / Beginner / Up to hard / Descents only)
//        - 7 grosse Rows (≥ 52 px Höhe) mit Swatch + Label + Hint + Checkbox
//      Live-Apply: jede Änderung schreibt sofort in den `trailFilterBus`.
//   3. Wenn ein Filter aktiv ist, zeigt die Pille einen kleinen orange Dot
//      oben rechts (analog zum Funnel-Icon auf TrailsPage).
//
// Die alte separate "Erklärungs-Modal" entfällt — die Hints stehen jetzt
// inline in jeder Row im Sheet, und die Quick-Presets erleichtern die
// häufigsten Fälle ohne dass der User über Difficulty-Definitionen
// nachdenken muss.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DIFFICULTY_LABEL,
  DIFFICULTY_ORDER,
  difficultySwatchBackground,
  type Difficulty,
} from '@adtu/shared';
import {
  trailFilterBus,
  emptyTrailFilter,
  type TrailFilter,
} from '../lib/trailFilterBus';
import { trailVisibilityBus } from '../lib/map/trailVisibilityBus';
import { useColorAssistMode } from '../lib/useUiSettings';
import { EVEREST_EASTER_EGG_EVENT } from '../lib/everestEasterEgg';

// Filterbare Difficulties = alle ausser 'unknown' (der ist Sentinel).
const LEGEND_DIFFICULTIES: Difficulty[] = DIFFICULTY_ORDER.filter((d) => d !== 'unknown');

/** Kurzbeschreibung pro Difficulty-Stufe — knapp, MTB-Kontext. */
const DIFFICULTY_HINT: Record<Difficulty, string> = {
  uphill:
    'Pink. Pedal-up access to a descent — not a descent rating itself.',
  easy: 'Green. Wide paths, small obstacles. For beginners.',
  intermediate: 'Blue. Narrower trails, some technical sections.',
  intermediate_plus:
    'Blue & red striped. Between intermediate and hard — rockier / steeper than blue, still doable without jumps.',
  hard: 'Red. Technical descents, steep, loose terrain, small drops.',
  expert: 'Black. Very steep, large rocks, drops, mandatory commitment.',
  extreme: 'Black & white. Pro-level — jumps, cliffs, no mistakes allowed.',
  unknown: 'Difficulty not specified.',
};

/** Vordefinierte Auswahl-Sets — das sind die "klugen" Presets. Die Reihenfolge
 *  hier wird auch im UI als Chip-Reihe verwendet (Links → Rechts).
 *
 *  Wave 14ca-fix4: "None" wurde wieder rausgenommen. Begründung: unsere
 *  Bus-Semantik nutzt "leerer Set" als Sentinel für "kein Filter aktiv"
 *  (= alle Trails sichtbar). Ein "None"-Preset würde leeren Set schreiben,
 *  was visuell als "alle ausgewählt" interpretiert wird — also exakt das
 *  Gegenteil von dem was der Label suggeriert. Die User-Intention "ich
 *  will gerade nichts sehen" lässt sich besser durch deaktivieren via
 *  einzelner Tap-Aktionen ausdrücken; ein "None"-Knopf ist erfahrungsgemäss
 *  ein selten benutztes Feature. */
const PRESETS: { id: string; label: string; difficulties: Difficulty[] }[] = [
  // "All" / "None" matchen NIE per equality gegen die aktuelle Auswahl
  // (siehe activePresetId — wir matchen nur 1:1 echte selected-Sets).
  // Highlight für diese zwei Knöpfe steuern wir manuell unten:
  // "None" über den lokalen `noneMode`-State im Sheet (UI-only Reset),
  // "All" matcht implizit über die size === 7 Heuristik.
  { id: 'all', label: 'All', difficulties: [...LEGEND_DIFFICULTIES] },
  // Wave 14cb-fix6: "None" wieder rein als reiner Sheet-Reset (User-Wunsch
  // "neben all brauche ich noch einen button wo nichts markiert ist, um zb
  // nur rot auszuwählen"). Implementiert UI-only via noneMode-State —
  // der Bus bleibt leer (= kein Filter, alle Trails sichtbar im Hintergrund),
  // aber die Checkboxen sind alle aus, damit der User frisch ein einzelnes
  // Häkchen setzen kann. Sobald er irgendwas tappt, wird das zur neuen
  // Auswahl und der noneMode-State endet.
  { id: 'none', label: 'None', difficulties: [] },
  { id: 'beginner', label: 'Beginner', difficulties: ['easy', 'intermediate'] },
  // Wave 14cb-fix5: "Up to hard" → "Pro" (User-Wunsch), und die Auswahl
  // verschiebt sich von "easy bis hard" zu "intermediate+ bis extreme" —
  // also alles ab dem blau-rot gestreiften Trail abwärts. Logik: ein Pro
  // will *nicht* Easy/Intermediate auf der Karte sehen, ihn interessiert
  // nur das Schwere.
  {
    id: 'pro',
    label: 'Pro',
    difficulties: ['intermediate_plus', 'hard', 'expert', 'extreme'],
  },
  // 5th preset is an Easter Egg — see EVEREST_EASTER_EGG_EVENT in
  // everestEasterEgg.ts. The chip carries no real difficulty selection
  // (`difficulties: []`); the click handler short-circuits in `applyPreset`
  // before touching the bus, dispatches the easter-egg event, and closes
  // the sheet. We keep it in PRESETS (instead of as a separate button)
  // so it visually rides the same chip-row as the real presets.
  { id: 'chuck', label: 'Chuck Norris', difficulties: [] },
];

/** Wave 14ca-fix4: Liest den UI-Display-State aus dem Bus.
 *
 *  Mapping Bus → Display:
 *    - Bus leer (kein Difficulty-Filter aktiv) → Display = "alle 7 ausgewählt"
 *    - Bus mit hideAll=true                     → Display = "leer" (None-Mode)
 *    - Bus mit n Difficulties (1 ≤ n ≤ 6)        → Display = genau diese n
 *    - Bus mit allen 7                            → Display = alle 7 (sollte nicht
 *      vorkommen, weil writeSelected normalisiert — aber tolerant gerendert)
 *
 *  Diese Inversion (leer ↔ alle) entkoppelt die Bus-Semantik (= "Constraint-
 *  Liste, leer heisst kein Constraint") von der UX. TrailsPage und MapPage
 *  interessieren sich nur für den Bus-Wert (inkl. hideAll), nicht den
 *  Display-State, also bleiben die kompatibel. */
function readSelected(): Set<Difficulty> {
  const f = trailFilterBus.get();
  if (f?.hideAll) return new Set<Difficulty>(); // None-Mode: nichts angekreuzt
  const stored = new Set(f?.difficulties ?? []);
  if (stored.size === 0) {
    return new Set(LEGEND_DIFFICULTIES);
  }
  return stored;
}

/** Schreibt die difficulty-Auswahl in den Bus. Lässt andere Felder
 *  (maxDistKm, query) unangetastet.
 *
 *  Wave 14ca-fix4: "Alle 7 ausgewählt" = "kein Filter aktiv" → leerer Set.
 *  Wave 14cf-fix15: ein hideAll-Flag im Bus überlebt Sheet-Schließen, damit
 *  die "None"-Auswahl persistiert wird (vorher war noneMode nur Sheet-State
 *  und ging beim Done-Click verloren). */
function writeSelected(next: Set<Difficulty>, opts?: { hideAll?: boolean }) {
  const hideAll = opts?.hideAll === true;
  // Wenn hideAll, bleibt difficulties auf leerem Set — semantisch der None-
  // Mode. Sonst normalisieren: alle 7 = leer = "kein Filter aktiv".
  const normalised = hideAll
    ? new Set<Difficulty>()
    : next.size === LEGEND_DIFFICULTIES.length
      ? new Set<Difficulty>()
      : next;
  const current = trailFilterBus.get() ?? emptyTrailFilter();
  const updated: TrailFilter = {
    ...current,
    difficulties: normalised,
    hideAll,
  };
  const isActive =
    updated.maxDistKm != null ||
    updated.difficulties.size > 0 ||
    updated.query.trim().length > 0 ||
    updated.hideAll === true;
  trailFilterBus.set(isActive ? updated : null);
}

/** Welcher Preset matcht die aktuelle Auswahl exakt? Wird verwendet, um den
 *  passenden Preset-Chip optisch zu highlighten. `null` wenn keiner matcht
 *  oder die Auswahl leer ist (dann wäre nur "None" / "All" matchend, aber
 *  die haben spezielles Action-Verhalten). */
function activePresetId(selected: Set<Difficulty>): string | null {
  for (const p of PRESETS) {
    if (p.difficulties.length !== selected.size) continue;
    let allMatch = true;
    for (const d of p.difficulties) {
      if (!selected.has(d)) {
        allMatch = false;
        break;
      }
    }
    if (allMatch) return p.id;
  }
  return null;
}

/**
 * @param embedded Wenn `true`, rendert die Trigger-Pille ohne eigenes dunkles
 *   Hintergrund-Chrome — sie sitzt in der vereinheitlichten BottomBar-Pille
 *   und teilt sich deren Hintergrund.
 */
export function DifficultyLegend({ embedded = false }: { embedded?: boolean } = {}) {
  const [open, setOpen] = useState(false);
  // Wave 14ca-fix64: bei Toggle-Flip re-rendern damit die Swatches in der
  // Trigger-Pille + Sheet die neue Palette zeigen.
  useColorAssistMode();

  // Bus-Subscription für den Aktiv-Indicator (orange Dot) und für das Sheet.
  const [selected, setSelected] = useState<Set<Difficulty>>(() => readSelected());
  useEffect(() => {
    setSelected(readSelected());
    return trailFilterBus.subscribe(() => setSelected(readSelected()));
  }, []);

  // Wave 14ca-fix64: Visibility-Bus mit beobachten, damit ein TrailsPage-
  // Filter ("Zeig nur diese 5 Trails") hier sichtbar als "aktiv" gilt
  // und vom Sheet aus zurueckgesetzt werden kann. User-Bug: "Filter aus
  // /trails laesst sich aus dem Quick-Filter nicht aufloesen".
  const [visibilitySize, setVisibilitySize] = useState<number>(
    () => trailVisibilityBus.get()?.size ?? 0,
  );
  useEffect(() => {
    setVisibilitySize(trailVisibilityBus.get()?.size ?? 0);
    return trailVisibilityBus.subscribe((s) =>
      setVisibilitySize(s?.size ?? 0),
    );
  }, []);

  // Wave 14ca-fix4: "Aktiv" heisst NICHT "Set ist nicht leer" (unsere
  // readSelected liefert ja immer mindestens alle 7 zurück), sondern
  // "Filter versteckt etwas" — also weniger als alle 7 ausgewählt.
  // Wave 14ca-fix64: ZUSAETZLICH gilt als aktiv, wenn ein
  // Visibility-Filter aus TrailsPage gesetzt ist.
  // Wave 14cf-fix15: ZUSAETZLICH gilt als aktiv, wenn hideAll im Bus = true.
  const hideAllActive = trailFilterBus.get()?.hideAll === true;
  const anyActive =
    (selected.size > 0 && selected.size < LEGEND_DIFFICULTIES.length) ||
    visibilitySize > 0 ||
    hideAllActive;

  // Stage 6m-fix5 (User-Wunsch): schmaler Farbstreifen + Funnel-Icon
  // daneben, damit der User sofort sieht "das ist ein Filter, nicht nur
  // eine Legende". Vorher war's eine 70-px-breite Rainbow-Pille die nur
  // implizit klickbar war — jetzt ist der Filter-Zweck durch das Icon
  // explizit. Visuell konsistent mit dem Funnel-Button auf TrailsPage.
  const triggerClass = embedded
    ? 'relative h-[22px] flex items-center gap-1.5 px-0.5'
    : 'relative h-[26px] rounded-md shadow-sm flex items-center gap-1.5 border border-white/10 bg-black/60 px-1.5';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={triggerClass}
        aria-label={
          anyActive
            ? `Filter trails by difficulty (${selected.size} selected)`
            : 'Filter trails by difficulty'
        }
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Filter by difficulty"
      >
        {/* Schmaler 7-Segment-Streifen — pure Color-Legende, deutlich
            schmaler als vorher (32 px statt 70 px). */}
        <span
          className="flex items-stretch overflow-hidden rounded-sm"
          style={{
            width: '32px',
            height: embedded ? '14px' : '16px',
          }}
          aria-hidden="true"
        >
          {LEGEND_DIFFICULTIES.map((d) => (
            <span
              key={d}
              className="flex-1 h-full"
              style={{ background: difficultySwatchBackground(d) }}
            />
          ))}
        </span>
        {/* Funnel-Filter-Icon — visuell identisch zu dem auf TrailsPage,
            damit der User die Filter-Intention sofort versteht. Kleiner
            als der TrailsPage-Knopf weil er Teil der schmalen BottomBar-
            Pille ist. Wenn ein Filter aktiv ist, wird das Icon orange. */}
        <svg
          width={embedded ? '14' : '16'}
          height={embedded ? '14' : '16'}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={anyActive ? 'text-orange-400' : 'text-white/85'}
        >
          <polygon points="22 3 2 3 10 12.5 10 19 14 21 14 12.5 22 3" />
        </svg>
        {/* Aktiv-Indicator: kleiner orange Dot rechts oben am Icon. Nur
            zusätzlich zur Funnel-Färbung — beide signalisieren "Filter
            aktiv", aber der Dot bleibt sichtbar wenn der Funnel über
            einer kontrastarmen Map-Stelle steht. */}
        {anyActive && (
          <span
            className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-orange-500 ring-2 ring-turbo-900"
            aria-hidden="true"
          />
        )}
      </button>

      {/* Wave 14cb-fix5: Sheet wird in document.body geportalt. */}
      {open &&
        createPortal(
          <DifficultyFilterSheet
            selected={selected}
            hideAll={hideAllActive}
            visibilitySize={visibilitySize}
            onChange={writeSelected}
            onClearAll={() => {
              trailFilterBus.set(null);
              trailVisibilityBus.clear();
            }}
            onClose={() => setOpen(false)}
          />,
          document.body,
        )}
    </>
  );
}

// ─── Bottom-Sheet ─────────────────────────────────────────────────────────

function DifficultyFilterSheet({
  selected,
  hideAll,
  visibilitySize,
  onChange,
  onClearAll,
  onClose,
}: {
  selected: Set<Difficulty>;
  /** Wave 14cf-fix15: aktueller hideAll-Wert aus dem Bus. Wenn true, ist
   *  "None" aktiv und alle Trails sind versteckt — die Checkboxen müssen
   *  alle leer sein, der "None"-Chip leuchtet. */
  hideAll: boolean;
  /** Wave 14ca-fix64: Anzahl an Trails, die aktuell durch trailVisibilityBus
   *  auf der Karte sichtbar gehalten werden. 0 = kein Visibility-Filter. */
  visibilitySize: number;
  onChange: (next: Set<Difficulty>, opts?: { hideAll?: boolean }) => void;
  /** Setzt sowohl Difficulty- als auch Visibility-Filter komplett zurueck. */
  onClearAll: () => void;
  onClose: () => void;
}) {
  // Display-Selection: bei hideAll immer leer; sonst wie vom Parent geliefert.
  const displaySelected = hideAll ? new Set<Difficulty>() : selected;

  const activePreset = hideAll ? 'none' : activePresetId(selected);

  /** Einzelne Difficulty toggeln. Beendet hideAll-Mode falls aktiv. */
  const toggle = (d: Difficulty) => {
    if (hideAll) {
      // Aus dem None-Mode raus: nur diese eine Difficulty sichtbar machen.
      onChange(new Set([d]), { hideAll: false });
      return;
    }
    const next = new Set(selected);
    if (next.has(d)) next.delete(d);
    else next.add(d);
    onChange(next, { hideAll: false });
  };

  /** Preset anwenden. "None"-Spezialfall (Wave 14cf-fix15): schreibt
   *  hideAll=true in den Bus → MapPage filtert alle Trails weg. Vorher
   *  war das nur ein UI-State der beim Sheet-Schließen verloren ging.
   *
   *  "Chuck Norris" ist Easter-Egg — kein Filter-Effekt, sondern dispatch
   *  des `EVEREST_EASTER_EGG_EVENT`-Window-Events; MapPage hört darauf,
   *  setzt den synthetischen Everest-Trail als selected und schaltet auf
   *  Satellite. Sheet schließt direkt — Bus bleibt unangetastet. */
  const applyPreset = (p: (typeof PRESETS)[number]) => {
    if (p.id === 'chuck') {
      window.dispatchEvent(new CustomEvent(EVEREST_EASTER_EGG_EVENT));
      onClose();
      return;
    }
    if (p.id === 'none') {
      // Wave 14cf-fix15: persistent hideAll im Bus, NICHT mehr nur Sheet-State.
      onChange(new Set(), { hideAll: true });
      return;
    }
    onChange(new Set(p.difficulties), { hideAll: false });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Filter trails by difficulty"
      onClick={onClose}
    >
      {/* Sheet-Container: rastet auf kleinen Screens am unteren Rand ein
          (Klassisches iOS-Bottom-Sheet-Pattern). Auf Tablets/Desktop
          zentriert. */}
      <div
        className="bg-turbo-900 border-t border-turbo-500/30 sm:border sm:rounded-2xl rounded-t-2xl w-full sm:max-w-md shadow-2xl flex flex-col"
        // safe-area-inset-bottom: iPhones mit Home-Indikator brauchen den
        // Polster, sonst sitzt der Close-Button direkt auf dem schwarzen
        // Strich.
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag-Indikator. */}
        <div className="flex justify-center pt-1.5 pb-0.5 sm:hidden">
          <span className="w-10 h-1 rounded-full bg-white/25" aria-hidden="true" />
        </div>

        {/* Header — kompakter (pt-0 + pb-2). */}
        <div className="flex items-center gap-2 px-4 pt-0.5 pb-2 border-b border-turbo-700/40">
          <h3 className="text-sm font-semibold flex-1">Filter by difficulty</h3>
          {selected.size > 0 && selected.size < LEGEND_DIFFICULTIES.length && (
            <span className="text-[11px] tabular-nums px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-200 border border-orange-500/40">
              {selected.size}/{LEGEND_DIFFICULTIES.length}
            </span>
          )}
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 -mr-2 flex items-center justify-center rounded-full text-white/70 hover:text-white hover:bg-white/10 text-xl leading-none"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Wave 14ca-fix64: "Alle Trails zeigen"-Button.
            Erscheint NUR wenn aktuell ein Filter wirksam ist — entweder
            ein Difficulty-Filter (size > 0 && < 7), ein Visibility-Filter
            aus /trails, oder hideAll (Wave 14cf-fix15). */}
        {(visibilitySize > 0 ||
          hideAll ||
          (selected.size > 0 && selected.size < LEGEND_DIFFICULTIES.length)) && (
          <div className="px-4 pt-2 pb-1">
            <button
              type="button"
              onClick={() => {
                onClearAll();
                // Sheet offen lassen — der User sieht sofort, dass alle
                // Checkboxen wieder voll sind und der Visibility-Hint weg
                // ist. Das ist eindeutiger als das Sheet zu schliessen.
              }}
              className="w-full h-11 rounded-xl bg-orange-500 hover:bg-orange-400 active:bg-orange-600 text-turbo-950 font-semibold text-[13px] flex items-center justify-center gap-2 border border-orange-400 shadow-sm"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M3 6h18" />
                <path d="M19 6V4a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
              <span>
                {visibilitySize > 0
                  ? `Filter aufheben — alle Trails zeigen (${visibilitySize} aktuell sichtbar)`
                  : 'Filter aufheben — alle Trails zeigen'}
              </span>
            </button>
          </div>
        )}

        {/* Quick-Presets — kompakter ohne "Quick presets"-Label, Chips
            kleiner (h-8 statt min-h-[44px]). Wave 14cb-fix6. */}
        <div className="px-4 pt-2 pb-2 border-b border-turbo-700/40">
          <div
            className="flex gap-1.5 overflow-x-auto pb-0.5 -mx-1 px-1"
            style={{ scrollbarWidth: 'none' }}
          >
            {PRESETS.map((p) => {
              const isActive = activePreset === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => applyPreset(p)}
                  aria-pressed={isActive}
                  className={`shrink-0 px-3 h-8 rounded-full text-[13px] font-medium border transition-colors flex items-center ${
                    isActive
                      ? 'bg-orange-500 border-orange-500 text-turbo-950'
                      : 'bg-turbo-700/40 border-turbo-700/60 text-white/85 hover:bg-turbo-700/70 active:bg-turbo-700/90'
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Difficulty-Liste — kompakter (py-1.5, kleinere Hint, single-line
            mit Truncate). Min-Höhe 44 px = iOS-HIG-Tap-Minimum. */}
        <ul
          className="overflow-y-auto"
          // Höher gesetzt da die Rows kompakter sind — auf iPhone 13+ passen
          // jetzt alle 7 ohne Scroll, auf SE/8 gerade noch.
          style={{ maxHeight: '70vh' }}
        >
          {LEGEND_DIFFICULTIES.map((d) => {
            const isOn = displaySelected.has(d);
            return (
              <li key={d}>
                <button
                  type="button"
                  onClick={() => toggle(d)}
                  aria-pressed={isOn}
                  aria-label={`${isOn ? 'Hide' : 'Show'} ${DIFFICULTY_LABEL[d]} trails`}
                  className={`w-full flex items-center gap-3 px-4 py-1.5 text-left min-h-[44px] transition-colors ${
                    isOn
                      ? 'bg-orange-500/10 active:bg-orange-500/20'
                      : 'hover:bg-turbo-700/30 active:bg-turbo-700/50'
                  }`}
                >
                  {/* Color-Swatch — kleiner (24 statt 28), kein mt-Offset
                      mehr da die Row jetzt items-center statt items-start ist. */}
                  <span
                    className="w-6 h-6 rounded-md flex-shrink-0 border border-white/20"
                    style={{ background: difficultySwatchBackground(d) }}
                    aria-hidden="true"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white leading-tight">
                      {DIFFICULTY_LABEL[d]}
                    </div>
                    {/* Hint: 1 Zeile, ellipsis falls zu lang. Spart vertikalen
                        Platz dramatisch — wer mehr will, kennt Difficulty-
                        Codes ohnehin oder kann den Wikipedia-Link der MTB-
                        Skala googeln. */}
                    <div className="text-[11px] text-white/55 leading-tight truncate">
                      {DIFFICULTY_HINT[d]}
                    </div>
                  </div>
                  <span
                    className={`shrink-0 w-6 h-6 rounded-md flex items-center justify-center transition-colors ${
                      isOn
                        ? 'bg-orange-500 border border-orange-500 text-turbo-950'
                        : 'bg-transparent border border-white/30 text-transparent'
                    }`}
                    aria-hidden="true"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="5 12 10 17 19 7" />
                    </svg>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        {/* Footer-Done-Button. Wave 14cb-fix6: kompakter (py-2 statt py-3,
            min-h 44 statt 48) damit das ganze Sheet ohne Scroll passt.
            Live-Apply heisst der Filter ist schon angewendet — der Button
            schliesst nur das Sheet, ist also "Done" und nicht "Apply". */}
        <div className="border-t border-turbo-700/40 px-4 py-2">
          <button
            type="button"
            onClick={onClose}
            className="w-full bg-turbo-700/60 hover:bg-turbo-700/80 active:bg-turbo-700 text-white font-medium rounded-xl py-2 transition-colors min-h-[44px]"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
